<template>
    <v-container>
        <div class="title">New Updates</div>
        <div class="body-2 mb-3">
            Spotlight on changes to the regenerative medicine standards landscape within the past 45 days. Select a featured standard for more detail or <a @click.stop="allStandards">access the comprehensive Standards Search</a>. These updates are also highlighted on the
            search page.
        </div>
        <v-row no-gutters>
            <v-col v-for="(tab, index) in tabs" :key="index" @click="activeTabIndex = index">
                <FeaturedTab :icon="icon(tab.value)" :label="tab.text" :count="count(tab.value)" :active="index === activeTabIndex"></FeaturedTab>
            </v-col>
        </v-row>
        <v-row no-gutters>
            <v-col>
                <v-card flat tile color="primary">
                    <v-card-text class="white--text py-0 px-3">{{ tabs[activeTabIndex].hint }}</v-card-text>
                </v-card>
            </v-col>
        </v-row>
        <SearchResultsTable :standards="filteredFeatures" :sortable="false" :show-featured-icons="false"></SearchResultsTable>
        <v-card flat tile outlined class="mt-1 pa-12" v-if="!filteredFeatures.length">
            <v-card-text class="text-center body-1">
                <div>No standards meet this criteria at this time.</div>
                <div>Use the <a @click.stop="allStandards">Standards Search</a> to find standards that meet your criteria.</div>
            </v-card-text>
        </v-card>
        <div class="ma-6 text-center">
            <v-btn tile large color="primary" class="large-button px-12" @click="allStandards()">
                <span class="title">Search All Standards</span>
                <v-icon right>mdi-arrow-right</v-icon>
            </v-btn>
        </div>
        <div class="title">Areas of Need</div>
        <v-row no-gutters align="end">
            <v-col>
                <div class="body-2">
                    To help identify and prioritize areas of need, SCB solicits input from the regenerative medicine community, including feedback on:
                </div>
                <ul class="body-2 mb-3 mx-6">
                    <li>The <span class="font-weight-bold">impact</span> that a standard could have on the availability of safe and high-quality regenerative medicine therapies</li>
                    <li>The level of <span class="font-weight-bold">urgency</span> with which a needed standard should to be pursued to mitigate risk and realize opportunities</li>
                </ul>
                <div class="body-2">
                    Based on the feedback, the chart below is updated semi-annually to reflect the community’s prioritization perspectives.
                </div>
            </v-col>
        </v-row>
        <UrgencyAndImpact id="priorities"></UrgencyAndImpact>
        <div class="title">Sector Snapshots</div>
        <v-row no-gutters align="end">
            <v-col>
                <div class="body-2">At-a-glance overview of existing and in-development standards and areas of standards need, broken down by sector and functional area.</div>
            </v-col>
            <v-col cols="auto">
                <a class="primary--text body-2 ml-3" @click="$root.$definitionsDialog.show()">
                    <span>Help with definitions</span>
                    <v-icon size="20" color="primary" class="ml-1">mdi-help-circle-outline</v-icon>
                </a>
            </v-col>
        </v-row>
        <SectorSnapshot v-for="(sector, index) in sectors" :key="index" :sectorIndex="index"></SectorSnapshot>
    </v-container>
</template>

<script>
    import { get } from 'vuex-pathify';
    import { features, icon } from '@/common';
    import FeaturedTab from '@/components/FeaturedTab';
    import SearchResultsTable from '@/components/SearchResultsTable';
    import SectorSnapshot from '@/components/SectorSnapshot';
    import UrgencyAndImpact from '@/components/UrgencyAndImpact';
    import { upperFirst } from 'lodash';

    export default {
        name: 'Home',
        components: { FeaturedTab, SearchResultsTable, SectorSnapshot, UrgencyAndImpact },
        data: () => ({
            activeTabIndex: 0
        }),
        computed: {
            tabs() {
                return features.slice(1);
            },
            activeTab() {
                return this.tabs[this.activeTabIndex];
            },
            filteredFeatures() {
                return this[`featured${upperFirst(this.activeTab.value)}`];
            },
            sectors: get('common@sectors'),
            featured: get('featured'),
            ...get('featured*')
        },
        watch: {
            activeTab: {
                immediate: true,
                handler(value) {
                    this.$gtag.event('view', { event_category: 'Feature Tab', event_label: value.text });
                }
            }
        },
        methods: {
            icon: icon,
            count(feature) {
                return this[`featured${upperFirst(feature)}`].length;
            },
            allStandards() {
                this.$store.commit('resetFilters', true);
                this.$store.dispatch('search').then(() => {
                    this.$router.push({ path: this.$store.get('searchPath') });
                });
            }
        }
    };
</script>

<style lang="scss" scoped>
    $table-spacing: 4px;

    .results ::v-deep table {
        border-spacing: 0 $table-spacing !important;
        margin-bottom: #{$table-spacing * -4};
    }

    .large-button {
        text-transform: none;
    }

    .plain-href {
        text-decoration: none;
    }
</style>
